import React, { ReactElement, useEffect, useState, useRef } from "react";
import { Controller, FieldError } from "react-hook-form";
import { Control } from "react-hook-form/dist/types/form";
import {
  StyledLabel,
  TextFieldWrapper,
  WarnLabel,
} from "../TextFieldController";
import { ClickAwayListener } from "@mui/material";
import { LabelValuePair } from "../staticSelect/LabelValuePair";
import styled, { useTheme } from "styled-components";
import { v4 as uuidv4 } from "uuid";
import TooltipNew from "../../common/TooltipNew";
import {
  faCaretDown,
  faCaretUp,
  faCheckCircle,
  faExclamation,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TextVariant, TypographyNew } from "../../typography/typography";
import Loader from "../../loader/Loader";
import { CoreRequest } from "../../../redux/config/serviceInstances";
import LinkStyled from "../../buttonsLinks/LinkStyled";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { LabelContainer, MenuItemsContainer } from "./SelectNewTextField";
import Authorized from "../../common/Authorized";
import { Role } from "../../../redux/api-model/organization/company";
import { backDrop, flapOut } from "../../../utils/z-index";
import useSelectColors from "../useSelectColors";

export const MainContainer = styled.div`
  position: relative;
  height: 76px;
`;

export const MenuContainer = styled.div<{
  backgroundColor: string;
  boxShadow: string;
  hoverBG: string;
  hoverBo: string;
  width?: string;
  open?: boolean;
}>`
  position: relative;
  width: calc(100% - 30px);
  min-height: 38px;
  max-height: 260px;
  overflow: auto;
  border: 1px solid ${(props) => props.theme.colors.grays4};
  background-color: ${(props) => props.backgroundColor};
  box-shadow: ${(props) => props.boxShadow};
  border-radius: ${(props) => props.theme.borderRadius.small};
  font-size: 15px;
  font-weight: 400;

  z-index: ${(props) => (props.open ? flapOut : "unset")};

  @media (min-width: 600px) {
    width: ${(props) => (props.width ? props.width : "304px")};
  }
  &:hover {
    background-color: ${(props) => props.hoverBG};
    cursor: pointer;
    border: ${(props) => props.hoverBo};
  }
  &:focus {
    outline-style: solid;
    outline-width: 2px;
    outline-color: ${(props) => props.theme.colors.primary1};
  }
`;

export const SelectedValueContainer = styled.div<{
  open: boolean;
}>`
  position: sticky;
  top: 0;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.color};
  min-height: 38px;
  padding: 0 10px 0 10px;
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
  z-index: ${(props) => (props.open ? backDrop : "unset")};
`;

export const MenuItem = styled.div<{ selected?: boolean; active?: boolean }>`
  position: relative;
  min-height: 40px;
  padding: 2px 10px 2px 10px;
  color: ${(props) =>
    props.selected || props.active
      ? props.theme.colors.primary1
      : props.theme.colors.grays1};
  display: flex;
  align-items: center;
  outline: none;
  background-color: ${(props) =>
    props.selected || props.active ? props.theme.colors.misc3 : "auto"};
  cursor: ${(props) => (props.active ? "pointer" : "default")};
`;

type SelectNewProps<T = string | number | null> = {
  responsive?: boolean;
  defaultValue?: T;
  placeholder?: string;
  label?: string;
  noIndicator?: boolean;
  width?: string;
  menuItems?: LabelValuePair<T>[];
  createLink?: string;
  labelsEndpoint?: string;
  tooltip?: string;
  errorMessage?: string;
  isInvalid?: boolean;
  isDirty?: boolean;
  error?: FieldError;
  onChange: (value: T) => void;
  value: T;
  noItemsHint?: {
    text: string;
    link: string;
    linkText: string;
  };
};

export const SelectNew = <T extends string | number | null>(
  props: SelectNewProps<T>
): React.ReactElement => {
  const theme = useTheme();
  const menuItemsContainerRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<{
    open: boolean;
    filled: boolean;
    menuItems: LabelValuePair<T>[];
    error: boolean;
    loading: boolean;
  }>({
    open: false,
    filled: false,
    menuItems: props.menuItems ? props.menuItems : [],
    error: false,
    loading: false,
  });
  const selectedItemIndex = state.menuItems.findIndex((item) => {
    return item.value == props.value;
  });
  const [activeItem, setActiveItem] = useState(selectedItemIndex);
  const isInvalidIndicatorShown =
    ((props.isInvalid && props.isDirty) || props.error) && !props.noIndicator;
  const isValidIndicatorShown =
    !props.isInvalid && props.isDirty && !props.noIndicator;

  const scrollTo = (index: number) => {
    if (state.open && index >= 0 && menuItemsContainerRef.current) {
      const activeElement = menuItemsContainerRef.current.children[
        index
      ] as HTMLDivElement;
      menuItemsContainerRef.current.parentElement?.scroll({
        top: activeElement.offsetTop,
      });
    }
  };

  useEffect(() => {
    scrollTo(selectedItemIndex);
  }, [selectedItemIndex, state.open]);

  useEffect(() => {
    if (props.menuItems?.length) {
      setState({ ...state, menuItems: props.menuItems });
    }
  }, [props.menuItems]);

  useEffect(() => {
    if (props.labelsEndpoint) {
      setState({ ...state, loading: true });
      CoreRequest.get<Array<LabelValuePair<T>>>(props.labelsEndpoint).then(
        (response) => {
          setState((prevState) => ({
            ...prevState,
            loading: false,
            menuItems: response.data,
          }));
        }
      );
    }
  }, [props.labelsEndpoint]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.code) {
      case "ArrowDown":
        e.preventDefault();
        if (!state.open) {
          setState({
            ...state,
            open: true,
          });
        }
        if (activeItem < state.menuItems.length - 1) {
          scrollTo(activeItem + 1);
          setActiveItem((prev) => prev + 1);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (activeItem > 0) {
          scrollTo(activeItem - 1);
          setActiveItem((prev) => prev - 1);
        }
        break;
      case "Space":
        e.preventDefault();
        setState({ ...state, open: true });
        break;
      case "Enter":
        e.preventDefault();
        if (state.open) {
          const value =
            menuItemsContainerRef.current?.children[activeItem].getAttribute(
              "data-value"
            );
          props.onChange(value as any);
        }
        setState({ ...state, open: !state.open });
        break;
      case "Tab":
      case "Escape":
        setState({ ...state, open: false });
        setActiveItem(selectedItemIndex);
        break;
    }
  };

  const { backgroundColor, color, boxShadow, hoverBG, hoverBo } =
    useSelectColors(state.open, state.filled);

  if (props.noItemsHint && state.menuItems.length === 0 && !state.loading) {
    return (
      <TypographyNew variant={TextVariant.body2} color={"darkorange"}>
        {props.noItemsHint.text}
        <br />
        <Authorized visibleFor={[Role.COMPANY_ACCESS]}>
          <LinkStyled to={props.noItemsHint?.link}>
            {props.noItemsHint?.linkText}
          </LinkStyled>
        </Authorized>
      </TypographyNew>
    );
  }

  return (
    <TextFieldWrapper responsive={props.responsive}>
      {props.label ? (
        <StyledLabel responsive>
          <LabelContainer>
            <label> {props.label}</label>&nbsp;
            {props.tooltip ? <TooltipNew message={props.tooltip} /> : <></>}
            {isValidIndicatorShown ? (
              <>
                &nbsp;
                <FontAwesomeIcon
                  icon={faCheckCircle as IconProp}
                  color={theme.colors.accent7}
                />
              </>
            ) : null}
            {isInvalidIndicatorShown ? (
              <>
                &nbsp;
                <FontAwesomeIcon
                  icon={faExclamationCircle as IconProp}
                  color={theme.colors.accent1}
                />
              </>
            ) : null}
          </LabelContainer>
        </StyledLabel>
      ) : null}
      <MenuContainer
        open={state.open}
        backgroundColor={backgroundColor}
        boxShadow={boxShadow}
        color={color}
        hoverBG={hoverBG}
        hoverBo={hoverBo}
        onKeyDown={handleKeyDown}
        onClick={() => {
          setState({ ...state, open: !state.open });
        }}
        width={props.responsive ? "100%" : props.width}
      >
        <SelectedValueContainer tabIndex={0} open={state.open}>
          <SelectedContent
            menuItems={state.menuItems}
            createLink={props.createLink}
          >
            <>
              {state.menuItems[selectedItemIndex]?.label ??
                props.placeholder ??
                ""}
              <FontAwesomeIcon
                style={{ marginLeft: "auto" }}
                icon={
                  state.open
                    ? (faCaretUp as IconProp)
                    : (faCaretDown as IconProp)
                }
                color={theme.colors.grays1}
              />
            </>
          </SelectedContent>
        </SelectedValueContainer>
        {state.open ? (
          <ClickAwayListener
            onClickAway={() => {
              setState({ ...state, open: false });
              setActiveItem(-1);
            }}
          >
            <MenuItemsContainer
              role="listbox"
              tabIndex={-1}
              ref={menuItemsContainerRef}
              onMouseLeave={() => setActiveItem(-1)}
            >
              {state.loading ? <Loader /> : <></>}
              {state.error ? (
                <>
                  <TypographyNew
                    variant={TextVariant.body2}
                    color={theme.colors.accent2}
                  >
                    Auswahl konnte nicht geladen werden.
                  </TypographyNew>
                  <FontAwesomeIcon
                    icon={faExclamation as IconProp}
                    color={"red"}
                  />
                </>
              ) : (
                <></>
              )}
              {state.menuItems.map((item, index) => {
                return (
                  <MenuItem
                    onMouseEnter={() => setActiveItem(index)}
                    onClick={() => props.onChange(item.value)}
                    tabIndex={activeItem === index ? 0 : -1}
                    role="option"
                    key={uuidv4()}
                    active={activeItem === index}
                    selected={item.value === props.value}
                    aria-selected={item.value === props.value}
                    data-value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                );
              })}
            </MenuItemsContainer>
          </ClickAwayListener>
        ) : (
          <></>
        )}
      </MenuContainer>
      {props.error && props.errorMessage ? (
        <WarnLabel>{props.errorMessage}</WarnLabel>
      ) : null}
    </TextFieldWrapper>
  );
};

export type Rules = {
  required?: boolean;
  minLength?: number;
  validate?: (value: any) => boolean;
};

type SelectNewControllerProps = {
  responsive?: boolean;
  control: Control<any>;
  fieldName: string;
  defaultValue?: number | null | string;
  placeholder: string;
  label?: string;
  onChange?: (...value: any[]) => void;
  width?: string;
  noIndicator?: boolean;
  menuItems?: LabelValuePair[];
  createLink?: string;
  labelsEndpoint?: string;
  tooltip?: string;
  className?: string;
  rules?: Rules;
  errorMessage?: string;
  noItemsHint?: {
    text: string;
    link: string;
    linkText: string;
  };
};

const SelectNewController: React.FC<SelectNewControllerProps> = ({
  className,
  fieldName,
  control,
  defaultValue,
  rules,
  ...rest
}) => {
  return (
    <MainContainer className={className}>
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({
          field: { onChange, value },
          fieldState: { error, isDirty, invalid },
        }) => {
          const onChangeHandler = (...params: any[]) => {
            onChange(...params);
            rest.onChange?.(...params);
          };

          return (
            <SelectNew
              {...rest}
              error={error}
              onChange={onChangeHandler}
              value={value}
              isDirty={isDirty}
              isInvalid={invalid}
            />
          );
        }}
      />
    </MainContainer>
  );
};

type SelectedContent = {
  children: ReactElement;
  menuItems: LabelValuePair[];
  createLink?: string;
};

function SelectedContent({ children, menuItems, createLink }: SelectedContent) {
  if (!menuItems.length && createLink) {
    return <LinkStyled to={createLink}>{children}</LinkStyled>;
  }

  return <>{children}</>;
}

export default SelectNewController;
